import { useDeviceVerifierStore } from "@/stores/deviceVerifier";
import { useSystemSettingsStore } from "@/stores/systemSettings";

/**
 * The "deviceClientId" is responsible for handling API requests and responses for 
 * the POS. If "X-CLIENT-ID" is not included in the header of the request,
 * then data won't be returned as the device(POS) will fail to be associated
 * to the overall system.
 * 
 * NOTE:
 * For this don't use "useDeviceVerifierStore" from Pinia directly.
 * Pinia "getActivePinia()" error due to store usage before app context initialization
 * or outside of a Vue component.
 */
export const getDeviceClientId = () => {
    const deviceVerifierInfo = localStorage.getItem("deviceVerifier");
    let deviceClientId = "";
    if (deviceVerifierInfo) {
        try {
            const parsed = JSON.parse(deviceVerifierInfo);
            deviceClientId = parsed.uuid || "";
        } catch (e) {
            console.error("Failed to parse deviceVerifier from localStorage:", e);
        }
    }
    return deviceClientId;
}
  
/**
 * Handle unverified device after the API returns a 401 Unauthorized error.
 * This is specifically for the case when the device (POS) was verified at first,
 * but from admin panel a new OTP had been generated and as a result 
 * this device(POS) is no longer verified.
 */
export const handleUnverifiedDeviceClient = (error) => {
    if (!(error.status)) return;
    const errorMessage = error.response?.data?.detail || "";
    const deviceVerifierStore = useDeviceVerifierStore();
    const systemSettingsStore = useSystemSettingsStore();
    if (error.status === 401) {
        if (errorMessage.trim().toLowerCase().includes("unverified client")) {
            const uuid = deviceVerifierStore.getUuid();
            if (uuid && uuid.trim() !== "") {
                /**
                 * First we reset the device verifier store.
                 * This will clear the current state of the device verifier.
                 * Then we set the error message in the device verifier store.
                 * This will trigger the verification modal to show up.
                 */
                deviceVerifierStore.resetVerifier();
                deviceVerifierStore.setErrorMessage(errorMessage);

                /**
                 * Reset system settings in localStorage.
                 * NOTE:
                 * Specially the client id, as it is used in other parts of the code. 
                 * if not handled then event handling will fail.
                 */
                systemSettingsStore.resetSystemSettings();
                
                /**
                 * Redirect to home page to show the verification modal.
                 */
                window.location.href = "/";
            }
        }
    } else if (error.status === 400) {
        /* Get the request URL from the config */
        const requestUrl = error.config?.url || "";
        /** 
         * Check if the route is for verifying OTP.
         * If the request URL includes "/kiosks/verify-otp", we proceed to next steps.
         */
        const isValidUrl = requestUrl.includes("/kiosks/verify-otp");
        if (!isValidUrl) {
            return;
        }
        /**
         * If the request URL is valid, we set the error message in the device verifier store.
         * This will trigger the verification modal to show up.
         */
        deviceVerifierStore.setErrorMessage(errorMessage);
    }
}
  
export const setDeviceVerifierJson = (customData = {}) => {
    const defaultData = {
      isVerified: false,
      showVerificationModal: true,
      uuid: "",
      errorMessage: "",
    };
  
    const finalData = {
      ...defaultData,
      ...customData,
    };
  
    localStorage.setItem("deviceVerifier", JSON.stringify(finalData));
};